import api from "@/services/api";
import { useQuery } from "@tanstack/vue-query";
import { reactive, computed } from "vue";
import { useAuthStore } from "@/store/auth";
import { formatNumber } from "@/utils/filters/numbers";

// credits fields are DRF-serialized Decimals: strings like "12.7500".
export interface FeatureTokenUsage {
  feature: string;
  tokensUsed: number;
  creditsUsed: string;
}

export interface UserTokenUsage {
  userId: number;
  userName: string;
  tokensUsed: number;
  creditsUsed: string;
  featureUsage: FeatureTokenUsage[];
}

export interface AITokenUsage {
  currentMonthUsage: number;
  monthlyLimit: number;
  isUnlimited: boolean;
  daysUntilRenewal: number;
  // The number the backend enforcement gate compares when credits are enforced,
  // and the number featureUsage sums exactly to (see backend/ai_agents/pricing.py
  // credits_for_source — the view derives this by summing the same per-source
  // helper the gate calls, so no rounding gap exists between headline and rows).
  currentMonthCreditsUsed: string;
  monthlyCreditAllowance: number | null; // null = unlimited
  creditEnforcementEnabled: boolean;
  featureUsage: FeatureTokenUsage[];
  userUsage: UserTokenUsage[];
}

export interface AIUsageSummary {
  used: number;
  limit: number | null; // null = unlimited
  isUnlimited: boolean;
  isExceeded: boolean;
  // Which currency the backend gate is actually enforcing right now. The
  // settings panel keys its labels off this so we never show a healthy
  // "3 / 5000 credits" bar while the token gate blocks every AI surface.
  unit: "credits" | "tokens";
}

// Credit-denominated summary. Used as the headline the feature table
// reconciles against; consumers that want to reflect the *enforcement*
// currency should call enforcedUsageSummary instead.
export function creditUsageSummary(
  data: AITokenUsage | undefined,
): AIUsageSummary | undefined {
  if (!data) return undefined;
  const used = Number(data.currentMonthCreditsUsed);
  const limit = data.monthlyCreditAllowance;
  return {
    used,
    limit,
    isUnlimited: limit === null,
    // `>=` matches the backend gate (an allowance of 0 blocks a zero-usage
    // org — the admin kill-switch).
    isExceeded: limit !== null && used >= limit,
    unit: "credits",
  };
}

function tokenUsageSummary(data: AITokenUsage): AIUsageSummary {
  return {
    used: data.currentMonthUsage,
    limit: data.isUnlimited ? null : data.monthlyLimit,
    isUnlimited: data.isUnlimited,
    isExceeded:
      !data.isUnlimited && data.currentMonthUsage >= data.monthlyLimit,
    unit: "tokens",
  };
}

// The summary in whichever currency the backend gate is enforcing. Flag off
// on prod today (AI_CREDIT_ENFORCEMENT_ENABLED=false), so the settings panel
// speaks tokens until the flip — otherwise a manager would see a healthy
// credit meter while every AI surface is blocked "monthly usage limit reached".
export function enforcedUsageSummary(
  data: AITokenUsage | undefined,
): AIUsageSummary | undefined {
  if (!data) return undefined;
  return data.creditEnforcementEnabled
    ? creditUsageSummary(data)
    : tokenUsageSummary(data);
}

// Mirrors the backend gate (token_limits.get_ai_token_limit_exceeded):
// credits once enforcement is flipped on, legacy tokens until then — so AI
// features are only disabled here when the backend would actually block.
export function isUsageLimitExceeded(data: AITokenUsage | undefined): boolean {
  return enforcedUsageSummary(data)?.isExceeded ?? false;
}

function getAITokenUsage(): Promise<AITokenUsage> {
  return api.get("/ai-agents/token-usage/").then((response) => response.data);
}

export function useAITokenUsageQuery() {
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey: ["aiTokenUsage"] as const,
      queryFn: getAITokenUsage,
      enabled: computed(() => authStore.isAuthed),
    }),
  );
}

export function useIsAITokenLimitExceeded() {
  const usage = useAITokenUsageQuery();
  const isExceeded = computed(() => isUsageLimitExceeded(usage.data));
  return { isExceeded, usage };
}

// One credit formatter for the whole app so a display change (sub-cent floor,
// locale rule, "-" for null) can't diverge between the settings panel, the
// chat header, and any future surface. The DRF payload gives us "12.7500" as
// a string; Number(undefined) is NaN, which we surface as "0" so a
// frontend-ahead-of-backend deploy shows something sane.
export function formatCredits(
  value: string | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "0";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "0";
  // A row valued under a cent but above zero would floor to "0" against a
  // non-zero Total — misread as "no charge". Reserve "0" for genuine zeros.
  if (n > 0 && n < 0.01) return "<0.01";
  return formatNumber(n);
}
